import * as React from 'react';

import { connect } from 'react-redux';
import { USER_TYPING_DEBOUNCE_TIME } from '@helpcenter/constants';
import { Subscription, Observable } from '@helpcenter/epics-rxjs';
import type { FieldFormProps } from '@helpcenter/field-types';
import { fetchKBSuggestionsSuccessAction } from 'epics/model/kb-suggestion';
import { debounce, isEmpty } from 'lodash';
import { searchKbs } from 'rest/search';
import type { State as AppState } from 'state';
import type { RequestCreateKBState } from 'state/persisted/request-create';
import { getPortalId } from 'state/selectors/context';
import { getKbSuggestions, getProjectId } from 'state/selectors/portal';
import type { KnowledgeArticle, KnowledgeBaseSearchResult } from 'state/ui/search';
import withAnalyticsEvents from '@atlaskit/analytics-next/withAnalyticsEvents';
import withAnalyticsContext from '@atlaskit/analytics-next/withAnalyticsContext';
import type { WithAnalyticsEventsProps } from '@atlaskit/analytics-next/withAnalyticsEvents';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { OPERATIONAL_EVENT_TYPE, TRACK_EVENT_TYPE } from '@atlassian/analytics-web-react';
import { sendEvent, trackError } from '@atlassian/help-center-common-util/analytics';
import { isNetworkOrClientErrorCode } from '@atlassian/help-center-common-util/error-codes';
import { SummaryDumbField } from './summary-dumb';

const HELPCENTER_KB_SUGGESTIONS_MIN_THREE_CHARS = 'helpcenter_kb_suggestions_min_three_chars';
const KB_SUGGESTIONS_MIN_QUERY_LENGTH = 3;

const isKbSuggestionsMinThreeCharsEnabled = (): boolean =>
	FeatureGates.checkGate(HELPCENTER_KB_SUGGESTIONS_MIN_THREE_CHARS);

export interface OwnProps extends FieldFormProps<string>, WithAnalyticsEventsProps {
	knowledgeBase?: RequestCreateKBState;
	defaultValue?: string;
	valueOverride?: string;
	requestTypeId?: number;
}

interface DispatchProps {
	fetchKBSuggestionsSuccess: typeof fetchKBSuggestionsSuccessAction;
}

interface StateProps {
	kbSuggestions: KnowledgeArticle[];
	portalId?: number;
	projectId?: number;
}

export type Props = OwnProps & DispatchProps & StateProps;

export class SummaryField extends React.PureComponent<Props> {
	subscription: Subscription;

	componentDidMount() {
		this.subscription = new Subscription();
	}

	componentWillUnmount() {
		this.subscription.unsubscribe();
		const { fetchKBSuggestionsSuccess, portalId } = this.props;
		fetchKBSuggestionsSuccess({ portalId, kbArticleSuggestions: [] });
	}

	fetchKbSuggestions = (inputValue: string) => {
		const { knowledgeBase, fetchKBSuggestionsSuccess, portalId, projectId, requestTypeId } =
			this.props;
		const trimmedInputValue = inputValue.trim();

		if (isEmpty(trimmedInputValue)) {
			return;
		}

		if (
			isKbSuggestionsMinThreeCharsEnabled() &&
			trimmedInputValue.length < KB_SUGGESTIONS_MIN_QUERY_LENGTH
		) {
			fetchKBSuggestionsSuccess({ portalId, kbArticleSuggestions: [] });
			return;
		}

		// projectId must be defined for the search endpoint to work
		if (projectId) {
			const subs = searchKbs({
				projectId,
				requestTypeId,
				labels: knowledgeBase ? knowledgeBase.labels : [],
				query: trimmedInputValue,
				resultLimit: 3,
			}).subscribe(
				(kbSearchResponse: KnowledgeBaseSearchResult) => {
					const meta = this.getAnalyticsEvent(kbSearchResponse.articles.length);
					if (meta?.analyticsSuccessEvent) {
						sendEvent(meta.analyticsSuccessEvent);
					}
					fetchKBSuggestionsSuccess({ portalId, kbArticleSuggestions: kbSearchResponse.articles });
				},
				(e) => {
					// TypeScript upgrade (v4.4.3). Please correct when you revisit this code.
					const meta = this.getAnalyticsEvent();
					// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
					if (meta?.analyticsFailureEvent && !isNetworkOrClientErrorCode(e.status)) {
						sendEvent(meta.analyticsFailureEvent);
					}
					trackError('kbsuggestions.fetch.failed', {}, e);
					return Observable.empty();
				},
			);

			this.subscription.add(subs);
		}
	};

	getAnalyticsEvent = (length?: number) => {
		const { createAnalyticsEvent } = this.props;
		return {
			analyticsSuccessEvent: createAnalyticsEvent?.({
				action: 'Fetch success',
				analyticsType: TRACK_EVENT_TYPE,
				actionSubjectId: 'kbSuggestion',
				actionSubject: 'kbSuggestionField',
				totalArticles: length,
			}),
			analyticsFailureEvent: createAnalyticsEvent?.({
				action: 'Fetch failed',
				analyticsType: OPERATIONAL_EVENT_TYPE,
			}),
		};
	};

	loadKbSuggestions = debounce(this.fetchKbSuggestions, USER_TYPING_DEBOUNCE_TIME);

	onChange = (
		name: string,
		value: string,
		notFromUserInteraction: boolean,
		additionalFieldData: { rawObject: object; rawValue: unknown },
	) => {
		const { onChange } = this.props;
		onChange && onChange(name, value, notFromUserInteraction, additionalFieldData);

		this.loadKbSuggestions(value);
	};

	render() {
		const { knowledgeBase, onFieldBlurredByUser, kbSuggestions, ...props } = this.props;
		return (
			<SummaryDumbField
				{...props}
				onChange={this.onChange}
				onFieldBlurredByUser={onFieldBlurredByUser}
				kbArticles={kbSuggestions}
			/>
		);
	}
}

export const connectedSummaryField = connect(
	(state: AppState) => {
		const portalId = getPortalId(state);
		const projectId = getProjectId(state, portalId);
		return {
			portalId,
			projectId,
			kbSuggestions: getKbSuggestions(state, portalId),
		};
	},
	{ fetchKBSuggestionsSuccess: fetchKBSuggestionsSuccessAction },
)(SummaryField);

export default withAnalyticsContext({ componentName: 'KbSuggestions' })(
	withAnalyticsEvents()(connectedSummaryField),
);
